import { TableComponent as DataTable } from 'components/TableComponent'
import moment from 'moment-timezone'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Badge, Card, CardText, Col, Collapse, Row } from 'reactstrap'

import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import PMBProfilePic from 'components/PMBProfilePic'
import LihatDetail from 'components/Icons/LihatDetail'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBSelect from 'components/PMBSelect'
import { useDebounce } from 'helpers'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import { ChevronDown, ChevronUp, Filter, X } from 'react-feather'
import { PMBDateRangePicker } from 'components/PMBNewDatePicker'
import { Navigate, useNavigate } from 'react-router-dom'
import { formatDatePMB } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_406, URL_DETAIL_TAGIHAN_TALANGIN } from 'utils/pathUrl'
import { useFetchTalanginInvoiceQuery } from './talanginInvoiceApiSlice'
import { setFilter } from './talanginSlice'
import CustomButton from 'components/Buttons/CustomButton'

const TalanginInvoice = ({ action, subject }) => {
  const navigate = useNavigate()
  const abilityCreate = useAbilityAccess({ action, subject })

  const intl = useIntl()
  const dispatch = useDispatch()
  const talanginTable = useSelector((s) => s.talanginTable)
  const datePickerRef = useRef(null)
  let [page, setPage] = useState({ page: talanginTable?.page })
  const savedFilters =
    JSON?.parse(localStorage?.getItem('talanginFilters')) || {}
  const [searchInput, setSearchInput] = useState(
    savedFilters?.pmbSearch ?? talanginTable?.pmbSearch ?? '',
  )
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [isShowFilter, setIsShowFilter] = useState(
    savedFilters?.isShowFilter ?? false,
  )
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedStart = savedFilters?.startDate
    const savedEnd = savedFilters?.endDate
    if (savedStart && savedEnd) {
      return [savedStart, savedEnd]
    }

    if (talanginTable.startDate && talanginTable.endDate) {
      return [talanginTable.startDate, talanginTable.endDate]
    }
    return [null, null]
  })

  const dataTalangin = useFetchTalanginInvoiceQuery({
    ...talanginTable,
    pmbSearch: talanginTable.pmbSearch,
    pmbLunas: talanginTable.selectedFilter === 'paid',
    pmbBelumLunas: talanginTable.selectedFilter === 'unpaid',
    pmbDueDateToday: talanginTable.selectedFilter === 'today',
    pmbLate: talanginTable.selectedFilter === 'late',
    pmbRollover: talanginTable.selectedFilter === 'rollOver',
    selectedRepo: talanginTable.selectedRepo,
    from: talanginTable.startDate
      ? moment
          .tz(talanginTable.startDate, 'Asia/Jakarta')
          .startOf('day')
          .utc()
          .format()
      : undefined,
    to: talanginTable.endDate
      ? moment
          .tz(talanginTable.endDate, 'Asia/Jakarta')
          .endOf('day')
          .utc()
          .format()
      : undefined,
    sortBy: talanginTable.sortBy || 'duedate',
    sortDir: talanginTable.sortDir || 'DESC',
  })
  const { isOpenSidebar, selectedRow, justRolledOver, statusSelected } =
    useSelector((e) => e.talanginInvoice)

  // const toggleSidebar = () => dispatch(toggleSidebarTalangin())

  const isMobile = screenWidth <= 768

  useEffect(() => {
    if (savedFilters && Object.keys(savedFilters).length) {
      // restore from savedFilter if exists
      if (savedFilters.startDate && savedFilters.endDate) {
        setSelectedDate([
          moment(savedFilters.startDate, 'YYYY-MM-DD').toDate(),
          moment(savedFilters.endDate, 'YYYY-MM-DD').toDate(),
        ])
      } else if (talanginTable.startDate && talanginTable.endDate) {
        setSelectedDate([
          moment(talanginTable.startDate, 'YYYY-MM-DD').toDate(),
          moment(talanginTable.endDate, 'YYYY-MM-DD').toDate(),
        ])
      } else {
        setSelectedDate([])
      }
    } else {
      if (talanginTable.startDate && talanginTable.endDate) {
        setSelectedDate([
          moment(talanginTable.startDate, 'YYYY-MM-DD').toDate(),
          moment(talanginTable.endDate, 'YYYY-MM-DD').toDate(),
        ])
      } else {
        setSelectedDate([])
      }
    }
  }, [talanginTable.startDate, talanginTable.endDate])

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Sync search input with redux pmbSearch (useful when navigating back)
  useEffect(() => {
    if (talanginTable?.pmbSearch !== undefined) {
      setSearchInput(talanginTable?.pmbSearch ?? '')
    }
  }, [talanginTable?.pmbSearch])

  // Restore saved filters to redux on mount
  useEffect(() => {
    if (savedFilters && Object.keys(savedFilters).length) {
      dispatch(
        setFilter({
          selectedFilter:
            savedFilters.selectedFilter ?? talanginTable.selectedFilter,
          selectedRepo: savedFilters.selectedRepo ?? talanginTable.selectedRepo,
          pmbSearch: savedFilters.pmbSearch ?? talanginTable.pmbSearch,
          pmbLunas: savedFilters?.pmbLunas ?? talanginTable.pmbLunas,
          pmbBelumLunas:
            savedFilters?.pmbBelumLunas ?? talanginTable.pmbBelumLunas,
          pmbDueDateToday:
            savedFilters?.pmbDueDateToday ?? talanginTable.pmbDueDateToday,
          pmbLate: savedFilters?.pmbLate ?? talanginTable.pmbLate,
          pmbRollover: savedFilters?.pmbRollover ?? talanginTable.pmbRollover,
          startDate: savedFilters.startDate ?? talanginTable.startDate,
          endDate: savedFilters.endDate ?? talanginTable.endDate,
          page: savedFilters.page ?? talanginTable.page,
          size: savedFilters.size ?? talanginTable.size,
          sortBy: savedFilters.sortBy ?? talanginTable.sortBy,
          sortDir: savedFilters.sortDir ?? talanginTable.sortDir,
        }),
      )
    }
  }, [])

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFilter({
        pmbSearch: value,
        page: 0,
        size: 10,
      }),
    )
  }, 600)

  const handleKeyDown = (e) => {
    if (e.key === ' ' && searchInput.length === 0) {
      e.preventDefault()
    }
  }

  const isDateFilterChanged = useMemo(() => {
    return Boolean(talanginTable.startDate && talanginTable.endDate)
  }, [talanginTable.startDate, talanginTable.endDate])

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (isDateFilterChanged) count += 1
    if (
      talanginTable.selectedFilter &&
      talanginTable.selectedFilter !== 'all'
    ) {
      count += 1
    }
    if (talanginTable.selectedRepo && talanginTable.selectedRepo !== 'all') {
      count += 1
    }
    if (talanginTable.pmbSearch && talanginTable.pmbSearch.trim() !== '') {
      count += 1
    }
    return count
  }, [
    isDateFilterChanged,
    talanginTable.selectedFilter,
    talanginTable.selectedRepo,
    talanginTable.pmbSearch,
  ])

  // Persist talangin filters to localStorage whenever relevant filters change
  useEffect(() => {
    const filtersToSave = {
      selectedFilter: talanginTable.selectedFilter,
      selectedRepo: talanginTable.selectedRepo,
      pmbSearch: talanginTable.pmbSearch,
      pmbLunas: talanginTable.pmbLunas,
      pmbBelumLunas: talanginTable.pmbBelumLunas,
      pmbDueDateToday: talanginTable.pmbDueDateToday,
      pmbLate: talanginTable.pmbLate,
      pmbRollover: talanginTable.pmbRollover,
      startDate: talanginTable.startDate,
      endDate: talanginTable.endDate,
      page: talanginTable.page,
      size: talanginTable.size,
      sortBy: talanginTable.sortBy,
      sortDir: talanginTable.sortDir,
      isShowFilter: isShowFilter,
      pmbSearchInput: searchInput,
    }
    localStorage.setItem('talanginFilters', JSON.stringify(filtersToSave))
  }, [
    talanginTable.selectedFilter,
    talanginTable.selectedRepo,
    talanginTable.pmbSearch,
    talanginTable.pmbLunas,
    talanginTable.pmbBelumLunas,
    talanginTable.pmbDueDateToday,
    talanginTable.pmbLate,
    talanginTable.pmbRollover,
    talanginTable.startDate,
    talanginTable.endDate,
    talanginTable.page,
    talanginTable.size,
    talanginTable.sortBy,
    talanginTable.sortDir,
    isShowFilter,
    searchInput,
  ])

  const handleFilterChange = (filter) => {
    const filterValue = filter?.value ?? 'all'
    dispatch(
      setFilter({
        selectedFilter: filterValue,
        pmbLunas: filterValue === 'paid',
        pmbBelumLunas: filterValue === 'unpaid',
        pmbDueDateToday: filterValue === 'today',
        pmbLate: filterValue === 'late',
        pmbRollover: filterValue === 'rollOver',
        page: 0,
        size: 10,
      }),
    )
  }

  const handleRepoFilterChange = (filter) => {
    const repoValue = filter?.value ?? 'all'
    dispatch(
      setFilter({
        selectedRepo: repoValue,
        page: 0,
        size: 10,
      }),
    )
  }

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleDateChange = (dates) => {
    if (!dates || dates.length === 0) {
      setSelectedDate([])
      dispatch(
        setFilter({
          startDate: null,
          endDate: null,
          page: 0,
          size: talanginTable?.size ?? 10,
        }),
      )
      return
    }

    if (dates.length < 2) {
      return
    }

    const normalizedDates = dates.map(
      (date, idx) => date ?? selectedDate?.[idx] ?? null,
    )

    if (normalizedDates.some((date) => !date)) {
      return
    }

    setSelectedDate(normalizedDates)
    dispatch(
      setFilter({
        startDate: moment(normalizedDates[0])
          .tz('Asia/Jakarta')
          .format('YYYY-MM-DD'),
        endDate: moment(normalizedDates[1])
          .tz('Asia/Jakarta')
          .format('YYYY-MM-DD'),
        page: 0,
        size: talanginTable?.size ?? 10,
      }),
    )
  }
  const clearDateFilter = () => {
    setSelectedDate([])
    datePickerRef.current?.flatpickr?.clear()

    dispatch(
      setFilter({
        startDate: null,
        endDate: null,
        page: 0,
      }),
    )

    const saved = JSON.parse(localStorage.getItem('talanginFilters')) || {}
    delete saved.startDate
    delete saved.endDate
    localStorage.setItem('talanginFilters', JSON.stringify(saved))
  }

  const handleClearFilters = () => {
    setSelectedDate([])
    datePickerRef.current?.flatpickr?.clear()
    setSearchInput('')
    dispatch(
      setFilter({
        selectedFilter: 'all',
        selectedRepo: 'all',
        pmbSearch: '',
        pmbLunas: false,
        pmbBelumLunas: false,
        pmbDueDateToday: false,
        pmbLate: false,
        pmbRollover: false,
        startDate: null,
        endDate: null,
        page: 0,
        size: talanginTable?.size ?? 10,
      }),
    )
    // Clear persisted filters for talangin
    localStorage.removeItem('talanginFilters')
  }

  const handleSort = (column, direction) => {
    dispatch(
      setFilter({
        sortBy: column.selector || 'duedate',
        sortDir: direction?.toLowerCase(),
        page: 0,
      }),
    )
  }

  const filterOptions = [
    { label: 'Semua', value: 'all' },
    { label: 'Jatuh Tempo Hari Ini', value: 'today' },
    { label: 'Terlambat', value: 'late' },
    { label: 'Lunas', value: 'paid' },
    { label: 'Belum Lunas', value: 'unpaid' },
    { label: 'Diperpanjang', value: 'rollOver' },
  ]

  const filterRepoOptions = [
    { label: 'Semua', value: 'all' },
    { label: 'Ya', value: 'yes' },
    { label: 'Tidak', value: 'no' },
  ]

  const conditionalRowStyles = [
    {
      when: (row) => justRolledOver === row?.id,
      style: {
        backgroundColor: '#F3FEFF',
      },
    },
  ]

  const columns = [
    {
      name: <span>Penjual</span>,
      selector: 'sellerName',
      sortable: true,
      cell: (row) => (
        <div className="flex-centered gap-2">
          <PMBProfilePic
            imgSrc={row?.sellerAvatar}
            altText={row?.sellerName}
            name={row?.sellerName}
            avatarSeed={row?.escrowpayment?.seller?.userId}
            width={20}
            height={20}
          />
          <span>{row?.sellerName}</span>
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: <span>Jatuh Tempo</span>,
      selector: 'duedate',
      sortable: true,
      cell: (row) => <span>{formatDatePMB(row?.dueDate, row?.dueDate)}</span>,
      minWidth: '150px',
    },
    {
      name: <span>Barang</span>,
      cell: (row) => {
        return (
          <div className="d-flex flex-column my-1">
            <span className="flex-row">{`${row?.escrowpayment?.order?.orderitem?.[0]?.item?.nameItem}`}</span>
            <div className="d-flex gap-2">
              <P size="small" className="text-sm text-primary">
                {row?.escrowpayment?.order?.orderitem?.[0]?.amount}{' '}
                {row?.escrowpayment?.order?.orderitem?.[0]?.unit} x{' '}
              </P>{' '}
              <P size="small" className="text-sm text-primary">
                Rp
                {newFormatThousandSeparator(
                  row?.escrowpayment?.order?.orderitem?.[0]?.sellPriceBeforeVAT
                    ? row?.escrowpayment?.order?.orderitem?.[0]
                        ?.sellPriceBeforeVAT /
                        row?.escrowpayment?.order?.orderitem?.[0]?.amount
                    : row?.escrowpayment?.order?.orderitem?.[0]?.price,
                  0,
                )}
              </P>
            </div>

            {row?.escrowpayment?.order?.orderitem?.length > 1 && (
              <div className="bg-gray-100 badge rounded-pill my-1">
                <span className="text-dark fw-medium">
                  + {row?.escrowpayment?.order?.orderitem?.length - 1} barang
                  lainnya
                </span>
              </div>
            )}
          </div>
        )
      },
      minWidth: '150px',
    },
    {
      name: (
        <div
          style={{
            textAlign: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          Total Tagihan
        </div>
      ),
      selector: 'escrowpaymentAmount',
      sortable: true,
      cell: (row) => {
        const totalAmount = row?.escrowpayment?.escrowcreditv2?.useFreeTx
          ? row?.escrowpayment?.escrowcreditv2?.totalVAT +
            (row?.escrowpayment?.amount +
              (row?.previousUserPinjamanId
                ? row?.escrowpayment?.escrowcreditv2?.margin2
                : row?.escrowpayment?.escrowcreditv2?.margin) ||
              row?.amountPlusInterest) +
            (row?.escrowpayment?.escrowcreditv2?.feeTx -
              row?.escrowpayment?.escrowcreditv2?.feeTxVAT) +
            (row?.partialpaymentData?.fine || row?.fineData?.totalFine)
          : row?.escrowpayment?.escrowcreditv2?.feeTxVAT +
            row?.escrowpayment?.escrowcreditv2?.totalVAT +
            (row?.escrowpayment?.amount +
              (row?.previousUserPinjamanId
                ? row?.escrowpayment?.escrowcreditv2?.margin2
                : row?.escrowpayment?.escrowcreditv2?.margin) ||
              row?.amountPlusInterest) +
            (row?.escrowpayment?.escrowcreditv2?.feeTx -
              row?.escrowpayment?.escrowcreditv2?.feeTxVAT) +
            (row?.partialpaymentData?.fine || row?.fineData?.totalFine)

        return (
          <div className="d-flex justify-content-end w-100">
            <span>
              Rp
              {newFormatThousandSeparator(isNaN(totalAmount) ? 0 : totalAmount)}
            </span>
          </div>
        )
      },
      minWidth: '150px',
    },
    {
      name: <span>Dibayar oleh</span>,
      cell: (row) =>
        row?.pmbactions ? (
          <div className="flex-centered gap-2">
            <PMBProfilePic
              imgSrc={row?.paidOffUserAvatar}
              altText={row?.pmbactions?.verifiedprofile?.name}
              name={row?.pmbactions?.verifiedprofile?.name}
              avatarSeed={row?.pmbactions?.verifiedprofile?.userId}
              width={20}
              height={20}
            />
            <span>{row?.pmbactions?.verifiedprofile?.name ?? '-'}</span>
          </div>
        ) : (
          <>
            <div className="">-</div>
          </>
        ),
      minWidth: '150px',
    },
    {
      name: <span>Status</span>,
      cell: (row) => {
        let tdd = moment(row.lateDueDate ?? row.dueDate).tz('Asia/Jakarta')
        let diffInDays = moment().tz('Asia/Jakarta').diff(tdd, 'days')

        // Terlambat x hari:
        if (
          (row.status?.toLowerCase() === 'pending' ||
            row.status?.toLowerCase() === 'disbursed') &&
          diffInDays > 0
        ) {
          return (
            <div className="d-flex gap-1">
              <Badge size="sm" disabled className="badge-danger">
                Terlambat {diffInDays + 1} hari
              </Badge>
              <i
                className="bi bi-exclamation-circle-fill text-danger"
                style={{ fontSize: '10px' }}
              ></i>
            </div>
          )
        }

        // Lunas
        if (
          row.status?.toLowerCase() === 'rollover' ||
          row.status?.toLowerCase() === 'paidoff'
        ) {
          return (
            <Badge size="sm" disabled className="badge-verify">
              Lunas
            </Badge>
          )
        }

        // Jatuh Tempo Hari Ini
        if (
          (row.status?.toLowerCase() === 'pending' ||
            row.status?.toLowerCase() === 'disbursed') &&
          diffInDays === 0
        ) {
          return (
            <div className="d-flex gap-1">
              <Badge size="sm" className="badge-danger">
                Jatuh tempo hari ini
              </Badge>
              <i
                className="bi bi-exclamation-circle-fill text-danger"
                style={{ fontSize: '10px' }}
              ></i>
            </div>
          )
        }

        // Diperpanjang (x) hari
        if (
          (row.status?.toLowerCase() === 'pending' ||
            row.status?.toLowerCase() === 'disbursed') &&
          row.previousUserPinjamanId &&
          diffInDays < 0
        ) {
          return (
            <Badge size="sm" className="badge-process">
              Diperpanjang {row?.escrowpayment?.escrowcreditv2?.loanDays2} hari
            </Badge>
          )
        }

        // Belum Lunas
        if (
          (row.status?.toLowerCase() === 'pending' ||
            row.status?.toLowerCase() === 'disbursed') &&
          !row.previousUserPinjamanId &&
          diffInDays < 0
        ) {
          return (
            <Badge size="sm" className="badge-warning">
              Belum Lunas
            </Badge>
          )
        }
      },
      minWidth: '150px',
    },
    {
      name: '',
      selector: (row) => (
        <div className="d-flex gap-2">
          {/* OLD CODE TO SHOW SIDEBAR DRAWER */}
          {/* <div
            onClick={() => {
              toggleSidebar()
              dispatch(setSelectedRow(row))
            }}
            style={{ cursor: 'pointer' }}
          >
            Lihat Detail
          </div> */}
          <LihatDetail
            onClick={() => {
              navigate(URL_DETAIL_TAGIHAN_TALANGIN, {
                state: {
                  row,
                },
              })
            }}
            id={row?.id}
            data-testid={`talangin-row-detail-${row.id}`}
          />
        </div>
      ),
      center: true,
      width: '100px',
    },
  ]

  if (!abilityCreate) {
    return <Navigate to={URL_406} replace={true} />
  }

  return (
    <>
      <div className="w-100 px-3 pb-3">
        {/* <SidebarTalanginInvoice
          isOpen={isOpenSidebar}
          toggle={toggleSidebar}
          row={selectedRow}
        /> */}
        <div className="d-flex align-items-start gap-2 pt-3">
          <BreadCrumbs breadCrumbActive={'Tagihan Talangin'} />
        </div>
        <Card className="border-0 shadow-sm" body>
          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <CardText tag="h4" className="mb-0">
              Tagihan Talangin
            </CardText>
            <div className="d-flex align-items-center gap-2">
              {activeFilterCount > 0 && (
                <CustomButton
                  onClick={handleClearFilters}
                  color="danger"
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    padding: 0,
                    minWidth: '40px',
                  }}
                >
                  <X size={16} />
                </CustomButton>
              )}
              <CustomButton
                onClick={() => setIsShowFilter((prevState) => !prevState)}
                color="primary"
                className="position-relative"
                outline
              >
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <Filter size={15} />
                  <P className="mb-0">Filter</P>
                  {activeFilterCount > 0 && (
                    <Badge
                      color="danger"
                      pill
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        fontSize: '10px',
                      }}
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                  {isShowFilter ? (
                    <ChevronUp size={16} className="ml-2" />
                  ) : (
                    <ChevronDown size={16} className="ml-2" />
                  )}
                </div>
              </CustomButton>
            </div>
          </div>
          {/* FILTERS */}
          <Collapse isOpen={isShowFilter}>
            <div
              className="border rounded p-3 bg-light mb-3 filter-collapse"
              style={{
                width: '100%',
                transition: 'all 0.3s ease-in-out',
                opacity: isShowFilter ? 1 : 0,
                transform: isShowFilter ? 'translateY(0)' : 'translateY(-10px)',
              }}
            >
              <Row className="g-2">
                <Col xs={12} md={4} lg={3}>
                  <PMBSearchInputGroup
                    label={'Cari'}
                    value={searchInput}
                    placeholder="Cari..."
                    onChange={(e) => onHandleSearchChange(e.target.value)}
                    maxWidth="100%"
                    onKeyDown={handleKeyDown}
                    testId="talangin-search-input"
                  />
                </Col>
                <Col xs={12} md="auto" lg="auto">
                  <PMBDateRangePicker
                    style={{ width: '100%', maxWidth: '100%' }}
                    label="Tanggal"
                    value={selectedDate}
                    onChange={handleDateChange}
                    format="yyyy-mm-dd"
                    onClear={clearDateFilter}
                  />
                </Col>
                <Col xs={12} md={4} lg={3}>
                  <p className="mb-1">Status</p>
                  <PMBSelect
                    style={{ width: isMobile ? '100%' : '200px' }}
                    isSearchable={false}
                    isClearable
                    onChange={handleFilterChange}
                    options={filterOptions}
                    testId="talangin-filter-status-select"
                    value={
                      talanginTable.selectedFilter &&
                      talanginTable.selectedFilter !== 'all'
                        ? filterOptions.find(
                            (opt) => opt.value === talanginTable.selectedFilter,
                          )
                        : null
                    }
                    placeholder="Semua Status"
                  />
                </Col>

                <Col xs={12} md={4} lg={3}>
                  <p className="mb-1">Repo</p>
                  <PMBSelect
                    style={{ width: isMobile ? '100%' : '200px' }}
                    isSearchable={false}
                    isClearable
                    onChange={handleRepoFilterChange}
                    options={filterRepoOptions}
                    value={
                      talanginTable.selectedRepo &&
                      talanginTable.selectedRepo !== 'all'
                        ? filterRepoOptions.find(
                            (opt) => opt.value === talanginTable.selectedRepo,
                          )
                        : null
                    }
                    placeholder="Semua Repo"
                  />
                </Col>
              </Row>
            </div>
          </Collapse>
          {/* END FILTERS */}

          <DataTable
            columns={columns}
            data={dataTalangin?.data?.items}
            progressPending={
              dataTalangin?.isLoading || dataTalangin?.isFetching
            }
            conditionalRowStyles={conditionalRowStyles}
            onSort={(column, direction) => handleSort(column, direction)}
            responsive
            persistTableHead
            defaultSortFieldId={2}
            defaultSortAsc={false}
            sortIcon={<ChevronDown />}
            sortServer
            pagination
            paginationServer
            paginationComponent={(props) => (
              <PMBPagination
                rowCount={dataTalangin?.data?.totalItems}
                currentPage={talanginTable?.page}
                rowsPerPage={talanginTable?.size}
                setCurrentPage={(e) => {
                  dispatch(
                    setFilter({
                      page: e,
                    }),
                  )
                }}
                setRowsPerPage={(e) => {
                  dispatch(
                    setFilter({
                      size: e,
                    }),
                  )
                }}
                props={props}
              />
            )}
          />
        </Card>
      </div>
    </>
  )
}

export default TalanginInvoice
